import type { ErrorCode } from '@velaigal/shared/errors';

export class ApiError extends Error {
  constructor(
    public code: ErrorCode,
    public status: number,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/*
 * THE ACTIVE COMPANY, threaded onto every request as ?companyId=.
 *
 * A recruiter can hold more than one active membership, and requireCompany() answers
 * 400 company_required — not a silently-picked default — on any tenant-scoped route that does
 * not say which. It has always accepted ?companyId=; until now nothing on the client could
 * supply it, so a two-company recruiter had a non-functional HR app rather than a
 * mis-defaulted one.
 *
 * It lives here, as a module-level value behind a setter, for two reasons:
 *   - Every client call already funnels through api() below, so this is one function rather
 *     than ~40 call sites, and a route added later inherits it without being told to.
 *   - api.ts must not import the React context: it is imported by plain .ts modules and by the
 *     context itself. AuthContext pushes the value in (setActiveCompanyId) instead of api.ts
 *     reaching out for it.
 *
 * Null is the normal state for a seeker, an admin, an unprovisioned recruiter and a signed-out
 * visitor; it is also what a suspended tenant's recruiter gets, since /api/me leaves a suspended
 * company out of `companies`. In every one of those cases the param is simply not sent.
 */
let activeCompanyId: string | null = null;

/** Set from AuthContext: on session load (validated against the membership list), when the
 *  picker changes it, and back to null on sign-out so the next account starts clean. */
export function setActiveCompanyId(id: string | null): void {
  activeCompanyId = id;
}

/* Every path passed to api() is a same-origin '/api/...' string and no call site builds a
 * companyId of its own, so the separator is the whole of the merge — and the `&` branch is LIVE
 * rather than defensive. Three families of call site already carry a query string when they get
 * here: jobsApi's hrApplicationsByStatus (`?status=`, unconditional — every load of the
 * shortlisted and rejected lists), jobsApi's listJobs (`?after=`, only past page one), and
 * adminApi's qs() helper (`?role=`/`?q=`/`?page=`, when any is set). An earlier revision of this
 * comment named `?after=` as the only one: false when it was written, and it named the
 * CONDITIONAL path rather than one that actually keeps the branch exercised. Do not trust that
 * list to have stayed complete either — grep for a backtick path containing '?' before assuming
 * anything about it. Routes that do not read companyId ignore it: every server query schema is a
 * plain z.object, which strips unknown keys rather than rejecting. */
function withActiveCompany(path: string): string {
  if (!activeCompanyId) return path;
  return `${path}${path.includes('?') ? '&' : '?'}companyId=${encodeURIComponent(activeCompanyId)}`;
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(withActiveCompany(path), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  let data: unknown = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = (data as { error?: { code?: ErrorCode; message?: string; fields?: Record<string, string> } } | null)?.error;
    throw new ApiError(err?.code ?? 'internal', res.status, err?.message ?? 'Something went wrong.', err?.fields);
  }
  if (data === null) {
    throw new ApiError('internal', res.status, 'Empty response from server.');
  }
  return data as T;
}

export function apiUrl(path: string): string {
  return path;
}
